import React, { Component } from 'react';
import StopCard from './StopCard';
import { loadGoogleMaps } from '../../../util/googleMapsLoader';
import { STOP_DISPLAY_STATES, STOP_STATE_VISUAL, deriveStopDisplayState } from '../../../util/routeStopState';
import { getBaseCoords } from '../../../util/locationUtils';

// Renders the day's route as a road-following polyline (Google Directions API,
// client-side per server spec) with numbered pins per stop. Live blue dot
// uses watchPosition while the map is in view; cleaned up on unmount or
// when the tech switches back to list mode.
//
// Active stop's StopCard sticks to the bottom for one-tap "Finish Stop" parity
// with list mode.

// Dispatch palette — used when a non-tech viewer is looking at a multi-tech
// location and the stop's assigned_technician_color isn't set. Indexed by
// tech id modulo length so each tech gets a stable color.
const DISPATCH_FALLBACK_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316']; // theme-ignore-line

const resolveStopColor = (stop, displayState, useDispatchColors) => {
  if (useDispatchColors) {
    if (stop.assigned_technician_color) return stop.assigned_technician_color;
    if (stop.assigned_technician_id != null) {
      const idx = Math.abs(parseInt(stop.assigned_technician_id, 10) || 0) % DISPATCH_FALLBACK_COLORS.length;
      return DISPATCH_FALLBACK_COLORS[idx];
    }
  }
  const visual = STOP_STATE_VISUAL[displayState] || STOP_STATE_VISUAL.up_next;
  return visual.color;
};

// theme-ignore-start
const buildMarkerSvg = (color, displayState, sequence) => {
  const isCompleted = displayState === STOP_DISPLAY_STATES.COMPLETED;
  const label = isCompleted ? '✓' : String(sequence ?? '');
  // Inline SVG: white circle, colored ring, sequence/checkmark in center.
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 36 36">
      <circle cx="18" cy="18" r="14" fill="#ffffff" stroke="${color}" stroke-width="3"/>
      <text x="18" y="23" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"
            font-size="14" font-weight="700" fill="${color}">${label}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

// Home-base marker — dark slate so it reads as "ground truth" distinct from
// the colored stop pins. Slightly larger so it pops at small zooms.
// theme-ignore-end
// theme-ignore-start
const BASE_MARKER_SVG = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
    <circle cx="20" cy="20" r="16" fill="#1f2937" stroke="#ffffff" stroke-width="3"/>
    <path fill="#ffffff" d="M20 11l-7 6v9h4v-6h6v6h4v-9z"/>
  </svg>
`)}`;
// theme-ignore-end

class RouteMapView extends Component {
  state = {
    mapsReady: false,
    mapError: null,
  };

  mapRef = React.createRef();
  map = null;
  markers = [];
  baseMarker = null;
  directionsRenderer = null;
  userMarker = null;
  watchId = null;

  async componentDidMount() {
    try {
      const maps = await loadGoogleMaps();
      this.maps = maps;
      this.setState({ mapsReady: true }, () => this.initMap());
      this.startWatchingPosition();
    } catch (err) {
      this.setState({ mapError: err.message || 'Could not load map.' });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.state.mapsReady && prevProps.stops !== this.props.stops) {
      this.renderRoute();
    }
  }

  componentWillUnmount() {
    if (this.watchId != null) navigator.geolocation.clearWatch(this.watchId);
    this.clearMarkers();
    if (this.directionsRenderer) this.directionsRenderer.setMap(null);
  }

  initMap = () => {
    const maps = this.maps;
    const { stops, currentLocation } = this.props;
    const baseCoords = getBaseCoords(currentLocation);

    // Center: average of stops (or base if no stops, or US center as last resort).
    const center = this.computeInitialCenter(stops, baseCoords);

    this.map = new maps.Map(this.mapRef.current, {
      center,
      zoom: 11,
      disableDefaultUI: false,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
    });

    this.directionsRenderer = new maps.DirectionsRenderer({
      map: this.map,
      suppressMarkers: true,         // we render our own numbered pins
      polylineOptions: {
        strokeColor: '#3B82F6', // theme-ignore-line
        strokeWeight: 4,
        strokeOpacity: 0.8,
      },
    });

    this.renderRoute();
  };

  computeInitialCenter = (stops, baseCoords) => {
    const withCoords = (stops || []).filter(s => s.customer?.lat != null && s.customer?.lng != null);
    if (withCoords.length > 0) {
      const avgLat = withCoords.reduce((sum, s) => sum + s.customer.lat, 0) / withCoords.length;
      const avgLng = withCoords.reduce((sum, s) => sum + s.customer.lng, 0) / withCoords.length;
      return { lat: avgLat, lng: avgLng };
    }
    if (baseCoords) return baseCoords;
    return { lat: 39.5, lng: -98.35 }; // US geographic center fallback
  };

  renderRoute = () => {
    if (!this.map || !this.maps) return;
    this.clearMarkers();

    const maps = this.maps;
    const stops = this.props.stops || [];
    const stopsWithCoords = stops.filter(s => s.customer?.lat != null && s.customer?.lng != null);

    // Home-base marker — anchors the route visually. Without it the tech
    // can't tell where they're starting/ending the day from on the map.
    const baseCoordsForMarker = getBaseCoords(this.props.currentLocation);
    if (baseCoordsForMarker) {
      this.baseMarker = new maps.Marker({
        position: baseCoordsForMarker,
        map: this.map,
        icon: { url: BASE_MARKER_SVG, scaledSize: new maps.Size(40, 40) },
        title: `Home base — ${this.props.currentLocation?.location_name || 'Base'}`,
        zIndex: 100,
      });
    }

    // Markers — numbered pins per stop. Color resolves to tech color for
    // dispatch viewers, otherwise state-based for tech's own route.
    const useDispatchColors = !!this.props.useDispatchColors;
    stopsWithCoords.forEach(stop => {
      const ds = deriveStopDisplayState(stop.state);
      const color = resolveStopColor(stop, ds, useDispatchColors);
      const title = useDispatchColors && stop.assigned_technician_name
        ? `${stop.sequence}. ${stop.customer?.name || ''} · ${stop.assigned_technician_name}`
        : `${stop.sequence}. ${stop.customer?.name || ''}`;
      const marker = new maps.Marker({
        position: { lat: stop.customer.lat, lng: stop.customer.lng },
        map: this.map,
        icon: { url: buildMarkerSvg(color, ds, stop.sequence), scaledSize: new maps.Size(36, 36) },
        title,
      });
      marker.addListener('click', () => {
        if (this.props.onSelectStop) this.props.onSelectStop(stop);
      });
      this.markers.push(marker);
    });

    // Polyline via Directions API — base → stop 1 → ... → stop N → base when
    // base coords are known. Falls back to stop-1 → stop-N otherwise.
    const baseCoords = getBaseCoords(this.props.currentLocation);
    if (stopsWithCoords.length >= 1) {
      const directionsService = new maps.DirectionsService();
      let origin, destination, waypoints;
      if (baseCoords) {
        origin = baseCoords;
        destination = baseCoords;
        waypoints = stopsWithCoords.map(s => ({
          location: { lat: s.customer.lat, lng: s.customer.lng },
          stopover: true,
        }));
      } else if (stopsWithCoords.length >= 2) {
        origin = stopsWithCoords[0].customer;
        destination = stopsWithCoords[stopsWithCoords.length - 1].customer;
        waypoints = stopsWithCoords.slice(1, -1).map(s => ({
          location: { lat: s.customer.lat, lng: s.customer.lng },
          stopover: true,
        }));
      }
      if (origin) {
        directionsService.route(
          {
            origin,
            destination,
            waypoints,
            travelMode: maps.TravelMode.DRIVING,
            optimizeWaypoints: false, // server already sequenced; keep that order
          },
          (result, status) => {
            if (status === 'OK' && this.directionsRenderer) {
              this.directionsRenderer.setDirections(result);
            }
            // Silent failure — markers still render without polyline.
          }
        );
      }
    }

    // Fit bounds to all markers + base
    if (stopsWithCoords.length > 0 || baseCoords) {
      const bounds = new maps.LatLngBounds();
      stopsWithCoords.forEach(s => bounds.extend({ lat: s.customer.lat, lng: s.customer.lng }));
      if (baseCoords) bounds.extend(baseCoords);
      this.map.fitBounds(bounds, 80);
    }
  };

  clearMarkers = () => {
    this.markers.forEach(m => m.setMap(null));
    this.markers = [];
    if (this.baseMarker) {
      this.baseMarker.setMap(null);
      this.baseMarker = null;
    }
  };

  startWatchingPosition = () => {
    if (!navigator.geolocation) return;
    this.watchId = navigator.geolocation.watchPosition(
      (position) => this.renderUserPosition(position),
      () => { /* silent — no live dot if denied/unavailable */ },
      { enableHighAccuracy: true, maximumAge: 8000, timeout: 10000 }
    );
  };

  renderUserPosition = (position) => {
    if (!this.map || !this.maps) return;
    const here = { lat: position.coords.latitude, lng: position.coords.longitude };
    if (this.userMarker) {
      this.userMarker.setPosition(here);
    } else {
      this.userMarker = new this.maps.Marker({
        position: here,
        map: this.map,
        zIndex: 9999,
        icon: {
          path: this.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: '#1d4ed8', // theme-ignore-line
          fillOpacity: 1,
          strokeColor: '#ffffff', // theme-ignore-line
          strokeWeight: 3,
        },
        title: 'Your location',
      });
    }
  };

  render() {
    const { mapError } = this.state;
    const {
      stops, activeStop, expandedStopId,
      onSelectStop, onStartTravel, onCheckIn, onStartService, onFinishStop, onToggleItem,
      currentUserId,
    } = this.props;

    if (mapError) {
      return (
        <div className="alert alert-warning m-3">
          <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2" />
          {mapError}
        </div>
      );
    }

    // Bottom-sheet card priority: explicitly-tapped stop > caller-supplied
    // activeStop > implicit "current" stop (in-progress / arrived).
    let stopForSticky = null;
    if (expandedStopId != null) {
      stopForSticky = (stops || []).find(s => s.request_service_id === expandedStopId);
    }
    if (!stopForSticky) {
      stopForSticky = activeStop || (stops || []).find(s => {
        const ds = deriveStopDisplayState(s.state);
        return ds === STOP_DISPLAY_STATES.IN_PROGRESS || ds === STOP_DISPLAY_STATES.ARRIVED;
      });
    }
    const isExpanded = stopForSticky && stopForSticky.request_service_id === expandedStopId;

    return (
      <div className="route-map-view" style={{ position: 'relative', width: '100%', height: 'calc(100vh - 140px)' }}>
        <div ref={this.mapRef} style={{ width: '100%', height: '100%' }} />

        {/* Bottom sheet — shows the tapped stop's card (expanded if the user
            tapped a marker) or implicitly the active stop. */}
        {stopForSticky && (
          <div
            style={{
              position: 'absolute',
              bottom: 12,
              left: 12,
              right: 12,
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              borderRadius: 8,
              background: 'var(--color-bg-card)',
              maxHeight: '70vh',
              overflowY: 'auto',
            }}
          >
            <StopCard
              stop={stopForSticky}
              expanded={isExpanded}
              onToggleExpand={onSelectStop}
              onStartTravel={onStartTravel}
              onCheckIn={onCheckIn}
              onStartService={onStartService}
              onFinishStop={onFinishStop}
              onToggleItem={onToggleItem}
              currentUserId={currentUserId}
            />
          </div>
        )}
      </div>
    );
  }
}

export default RouteMapView;
